// src/components/SplitView.tsx
// Resizable two-panel layout for viewing detail alongside list/board views.
// Uses react-resizable-panels for the drag handle.

import { useEffect, useCallback, type ReactNode } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SplitViewProps {
  /** Content for the main (left) panel */
  children: ReactNode;
  /** Content for the detail (right) panel - null means closed */
  detail: ReactNode | null;
  /** Called when the detail panel should close */
  onClose: () => void;
  /** Minimum size of main panel in percentage (default: 30) */
  minMainSize?: number;
  /** Default size of detail panel in percentage (default: 45) */
  defaultDetailSize?: number;
}

// ---------------------------------------------------------------------------
// Close icon
// ---------------------------------------------------------------------------

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SplitView({
  children,
  detail,
  onClose,
  minMainSize = 30,
  defaultDetailSize = 45,
}: SplitViewProps) {
  const { t } = useI18n();

  // Escape to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && detail) {
        e.stopPropagation();
        onClose();
      }
    },
    [detail, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // No detail panel — render children at full width
  if (!detail) {
    return <div className="h-full w-full">{children}</div>;
  }

  return (
    <>
      {/* ── Desktop: side-by-side resizable panels (md+) ────────────── */}
      <div className="hidden md:flex h-full w-full">
        <Group orientation="horizontal" id="split-view">
          {/* Main panel */}
          <Panel
            minSize={`${minMainSize}%`}
            defaultSize={`${100 - defaultDetailSize}%`}
          >
            <div className="h-full overflow-auto">{children}</div>
          </Panel>

          {/* Resize handle */}
          <Separator
            className={cn(
              'w-1 shrink-0 bg-border transition-colors',
              'hover:bg-primary focus-visible:bg-primary',
              'cursor-col-resize',
            )}
          />

          {/* Detail panel */}
          <Panel
            minSize="25%"
            defaultSize={`${defaultDetailSize}%`}
          >
            <div
              className="relative h-full overflow-auto border-l border-border bg-background"
              role="complementary"
              aria-label={t('task.splitDetail')}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'absolute right-2 top-2 z-10',
                  'inline-flex items-center justify-center rounded-md p-1',
                  'text-muted-foreground hover:text-foreground hover:bg-muted',
                  'transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
                aria-label={t('common.close')}
              >
                <XIcon className="h-4 w-4" />
              </button>

              {detail}
            </div>
          </Panel>
        </Group>
      </div>

      {/* ── Mobile: full-screen overlay (< md) ──────────────────────── */}
      <div className="md:hidden h-full w-full">
        {/* Main content underneath */}
        <div className="h-full overflow-auto">{children}</div>

        {/* Overlay */}
        <div className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" aria-hidden="true" />

        <div
          className="fixed inset-0 z-50 flex flex-col bg-background"
          role="complementary"
          aria-label={t('task.splitDetail')}
        >
          {/* Mobile close bar */}
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="text-sm font-medium text-foreground">{t('task.details')}</span>
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'inline-flex items-center justify-center rounded-md p-1',
                'text-muted-foreground hover:text-foreground hover:bg-muted',
                'transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              aria-label={t('common.close')}
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable detail content */}
          <div className="flex-1 overflow-auto">{detail}</div>
        </div>
      </div>
    </>
  );
}

export default SplitView;
